import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useQuiz } from "@/hooks/useQuiz";
import type { QuizState, Test } from "@/types";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";

export default function TestAccess() {
  const { slug = "" } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { save, getSaved } = useQuiz(slug);

  const [test, setTest] = useState<Test | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [nome, setNome] = useState("");
  const [cognome, setCognome] = useState("");
  const [progetto, setProgetto] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.get<Test>(`/tests/${slug}`)
      .then((t) => { if (!cancelled) setTest(t); })
      .catch((e) => { if (!cancelled) setLoadError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  // Recovery: se già in corso, salta direttamente al runner
  useEffect(() => {
    const saved = getSaved();
    if (saved) navigate(`/area/test/${saved.submissionId}`, { replace: true });
  }, [getSaved, navigate]);

  function validate() {
    const e: Record<string, string> = {};
    if (nome.trim().length < 1) e.nome = "Inserisci il tuo nome";
    if (cognome.trim().length < 1) e.cognome = "Inserisci il tuo cognome";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleStart(ev: React.FormEvent) {
    ev.preventDefault();
    if (!validate()) return;
    setSending(true);
    setServerError(null);
    try {
      const state = await api.post<QuizState>(`/tests/${slug}/start`, {
        nomeAnonimo: nome.trim(),
        cognomeAnonimo: cognome.trim(),
        nomeProgetto: progetto.trim() || undefined,
      });
      save(state.submissionId);
      navigate(`/area/test/${state.submissionId}`, { replace: true });
    } catch (e: any) {
      setServerError(e.message ?? "Errore avvio test");
    } finally {
      setSending(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size={32} />
      </div>
    );
  }

  if (loadError || !test) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <Card className="max-w-md text-center">
          <div className="text-4xl mb-3">🚫</div>
          <p className="text-secondary">{loadError ?? "Test non trovato"}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10">
      <Card className="max-w-lg w-full space-y-6">
        <header className="space-y-2">
          <div className="font-mono text-xs uppercase tracking-wider text-secondary">
            Test di ingresso
          </div>
          <h1 className="text-3xl font-serif text-primary">{test.titolo}</h1>
          {test.descrizione && (
            <p className="text-secondary">{test.descrizione}</p>
          )}
          <div className="flex gap-3 text-xs font-mono text-muted pt-2">
            {test._count?.questions != null && (
              <span>{test._count.questions} domande</span>
            )}
            {test.durataMinuti && <span>· {test.durataMinuti} min</span>}
          </div>
        </header>

        <form onSubmit={handleStart} className="space-y-4">
          <Input
            label="Nome"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            error={errors.nome}
            autoFocus
          />
          <Input
            label="Cognome"
            value={cognome}
            onChange={(e) => setCognome(e.target.value)}
            error={errors.cognome}
          />
          <Input
            label="Nome del progetto (opzionale)"
            value={progetto}
            onChange={(e) => setProgetto(e.target.value)}
          />

          {serverError && (
            <div className="text-sm text-red-400 font-mono">{serverError}</div>
          )}

          <Button type="submit" loading={sending} className="w-full">
            Inizia il test →
          </Button>
        </form>
      </Card>
    </div>
  );
}
